import { HttpClient, HttpParams } from '@angular/common/http';
import { PreMoldFilters } from '../models/PreMoldFilters';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Injectable, inject } from '@angular/core';
import {
  PaginatedCustomMoldResponse,
  PaginatedFinishedProductResponse,
  PaginatedPreMoldTreadResponse,
  CasingSizeFiltersResponse,
  CasingCreditFiltersResponse,
  PaginatedCasingCreditResponse,
  PaginatedCasingCreditAssignedCustomerResponse,
  PaginatedCasingSizeResponse,
  PaginatedFinishedProductInfo,
  CasingCreditDetailsFiltersResponse,
  PaginatedCasingCreditDetailsResponse,
  PaginatedCasingSizeInfo,
  PaginatedPreMoldInfo,
  PaginatedMakeResponse,
  MakeFiltersResponse,
  PaginatedMakeInfo,
  CasingGrade,
  CasingGradeFiltersResponse,
  PaginatedFreeRepairResponse,
  FreeRepairFiltersResponse,
  PaginatedCustomMoldInfo,
  PaginatedCasingCreditRepairResponse,
  CasingCreditRepairFiltersResponse,
  PaginatedCasingCreditRepairDetailsResponse,
  CasingCreditRepairDetailsFiltersResponse,
  DisposalCharge,
  DisposalChargesResponse,
  PaginatedRejectReasonResponse,
  RejectReasonFiltersResponse,
  CasingGradeTableFiltersResponse,
  PaginatedCasingGradeTableResponse,
  PaginatedCasingGradeTableAssignedCustomerResponse,
  PaginatedCasingGradeSpecificationResponse,
  PaginatedCasingCreditDetails,
  RepairFiltersResponse,
  PaginatedRejectReasonInfo,
  RepairInfo,
  RepairResponse,
  CasingCreditRepairGridFiltersResponse,
  PaginatedFreeRepairDetailsResponse,
  PaginatedPreferredTreadResponse,
  PaginatedPreferredTreadSpecificationResponse,
  PreferredTread,
  PreferredTreadsFiltersResponse,
  PreferredTreadSpecification,
  PreferredTreadSpecificationDelete,
  PreferredTreadWithSpecRequest,
  PreferredTreadWithSpecResponse,
  Wheel,
  WheelFiltersResponse,
  WheelColor,
  WheelColorFiltersResponse,
  PaginatedWheelResponse,
  PaginatedWheelColorResponse,
  PaginatedCasingDefinitionResponse,
  FactoryCodeFiltersResponse,
  PaginatedFactoryCodeInfoResponse,
  PaginatedAdjustmentReasonInfoResponse,
  ReferenceInfoResponse,
  AdjustmentReasonFiltersResponse,
  PaginatedCasingFitmentResponse,
  CasingPurchaseFiltersResponse,
  PaginatedCasingPurchaseResponse,
  CasingPurchaseDetailsFiltersResponse,
  PaginatedCasingPurchaseDetailsResponse,
  PaginatedCasingPurchaseDetails
} from '../models/response.model';
import { AssignToPlantsFinishedProduct } from '../models/AssignToPlantsFinishedProduct';
import { Store } from '@ngxs/store';
import { UserState } from '../../../shared/state/UserState';
import { map } from 'rxjs/operators';
import { CreateCasingCredit } from '../models/create-casing-credit.model';
import { UpdateFreeRepair } from '../models/update-free-repair.model';

@Injectable({
  providedIn: 'root'
})
export class ProductDataService {
  public http = inject(HttpClient);
  public store = inject(Store);

  constructor() {}

  /**
   * Fetch pre-mold lines with optional projection of fields.
   * If caller supplies a non-empty `fields` list that does NOT contain 'treadImage',
   * the backend will strip image data to avoid large payloads (used for exports).
   */
  getPreMoldLines(
    filters: PreMoldFilters,
    pageNumber: number,
    pageSize: number,
    fields?: string[],
    excludeImages?: boolean
  ): Observable<PaginatedPreMoldTreadResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllPreMolds}`;

    const filteredParams = Object.entries(filters || {}).reduce((acc, [key, value]) => {
      if (value != null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as any);

    const params: any = {
      ...filteredParams,
      page: pageNumber,
      pageSize
    };

    // If caller provided fields to project, include as comma-separated string under `fields` key
    if (fields && fields.length > 0) {
      params.fields = fields.join(',');
    }

    // If caller explicitly requests to exclude images, add that flag so backend can strip image data
    if (excludeImages) {
      params.excludeImages = 'true';
    }

    return this.http.get<PaginatedPreMoldTreadResponse>(url, { params });
  }

  /**
   * Fetch finished product lines with optional projection of fields.
   * @param filters filter object
   * @param pageNumber page number
   * @param pageSize page size
   * @param fields optional list of fields to request from the backend (comma separated in query string)
   */
  getFinishedProductLines(
    filters: any,
    pageNumber: number,
    pageSize: number,
    fields?: string[]
  ): Observable<PaginatedFinishedProductResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllFinishedProducts}`;
    const filteredParams = Object.entries(filters || {}).reduce((acc, [key, value]) => {
      if (value != null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as any);
    const params: any = {
      ...filteredParams,
      page: pageNumber,
      pageSize
    };
    // If caller provided fields to project, include as comma-separated string under `fields` key
    if (fields && fields.length > 0) {
      params.fields = fields.join(',');
    }
    return this.http.get<PaginatedFinishedProductResponse>(url, { params });
  }

  assignPlantToFinishedProduct(payload: AssignToPlantsFinishedProduct): Observable<string> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.updatePlantAssociationForFinishedProduct}`;
    return this.http.post<string>(url, payload);
  }

  updateFinishedProducts(payload: PaginatedFinishedProductInfo): Observable<PaginatedFinishedProductResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.updateFinishedProducts}`;
    return this.http.post<PaginatedFinishedProductResponse>(url, payload);
  }

  getCasingSizes(filters: any, pageNumber: number, pageSize: number): Observable<PaginatedCasingSizeResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllCasingSizes}`;
    const filteredParams = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value != null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as any);
    const params: any = {
      ...filteredParams,
      page: pageNumber,
      pageSize
    };
    return this.http.get<PaginatedCasingSizeResponse>(url, { params });
  }

  updateCasingSizes(payload: PaginatedCasingSizeInfo): Observable<PaginatedCasingSizeResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.updateCasingSizes}`;
    return this.http.post<PaginatedCasingSizeResponse>(url, payload);
  }

  getCasingSizesFilters(): Observable<CasingSizeFiltersResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllCasingSizesFilter}`;
    return this.http.get<CasingSizeFiltersResponse>(url);
  }

  getDisposalCharges(): Observable<DisposalChargesResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllDisposalCharges}`;
    return this.http.get<DisposalChargesResponse>(url);
  }

  updateDisposalCharges(payload: DisposalCharge[]): Observable<DisposalChargesResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.updateDisposalCharges}`;
    return this.http.post<DisposalChargesResponse>(url, payload);
  }

  getCustomMoldLines(filters: any, pageNumber: number, pageSize: number): Observable<PaginatedCustomMoldResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllCustomMolds}`;
    const filteredParams = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value != null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as any);
    const params: any = {
      ...filteredParams,
      page: pageNumber,
      pageSize
    };
    return this.http.get<PaginatedCustomMoldResponse>(url, { params });
  }

  getMakeLines(filters: any, pageNumber: number, pageSize: number): Observable<PaginatedMakeResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllMakes}`;

    const filteredParams = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value != null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as any);

    const params: any = {
      ...filteredParams,
      page: pageNumber,
      pageSize
    };

    return this.http.get<PaginatedMakeResponse>(url, { params });
  }

  updateMakes(payload: PaginatedMakeInfo): Observable<PaginatedMakeResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.updateMakes}`;
    return this.http.post<PaginatedMakeResponse>(url, payload);
  }

  updateRepairs(payload: RepairInfo): Observable<RepairResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.updateRepairs}`;
    return this.http.post<RepairResponse>(url, payload);
  }

  getMakeFilters(): Observable<MakeFiltersResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllMakeFilters}`;
    return this.http.get<MakeFiltersResponse>(url);
  }
  updatePreMolds(payload: PaginatedPreMoldInfo): Observable<PaginatedPreMoldTreadResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.updatePreMolds}`;
    return this.http.post<PaginatedPreMoldTreadResponse>(url, payload);
  }

  getCasingCredits(filters: any, pageNumber: number, pageSize: number): Observable<PaginatedCasingCreditResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllCasingCredits}`;
    const filteredParams = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value != null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as any);
    const params: any = {
      ...filteredParams,
      page: pageNumber,
      pageSize
    };
    return this.http.get<PaginatedCasingCreditResponse>(url, { params });
  }

  casingCreditCodeExists(code: string): Observable<boolean> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.casingCreditCodeExists}`;
    return this.http.get<boolean>(url, { params: { code } });
  }

  freeRepairCodeExists(code: string): Observable<boolean> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.freeRepairCodeExists}`;
    return this.http.get<boolean>(url, { params: { code } });
  }

  getCasingCreditDetailsSearchFilters(casingCreditId: string): Observable<CasingCreditDetailsFiltersResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllCasingCreditDetailsFilter}`;
    const params = { casingCreditId };
    return this.http.get<CasingCreditDetailsFiltersResponse>(url, { params });
  }

  getFreeRepairDetails(freeRepairId: string): Observable<PaginatedFreeRepairDetailsResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getFreeRepairDetails}`;
    const params = { freeRepairId };
    return this.http.get<PaginatedFreeRepairDetailsResponse>(url, { params });
  }

  getFreeRepairDetailsForCreate(): Observable<PaginatedFreeRepairDetailsResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getFreeRepairDetailsForCreate}`;
    return this.http.get<PaginatedFreeRepairDetailsResponse>(url);
  }

  getCasingCreditRepairGridSearchFilters(): Observable<CasingCreditRepairGridFiltersResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getCasingCreditRepairGridSearchFilters}`;
    return this.http.get<CasingCreditRepairGridFiltersResponse>(url);
  }

  getCasingCreditDetails(
    filters: any,
    casingCreditPk: string,
    pageNumber: number,
    pageSize: number
  ): Observable<PaginatedCasingCreditDetailsResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getCasingCreditDetails}`;
    const filteredParams = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value != null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as any);
    const params: any = {
      ...filteredParams,
      casingCreditPk: casingCreditPk,
      page: pageNumber,
      pageSize
    };
    return this.http.get<PaginatedCasingCreditDetailsResponse>(url, { params });
  }

  getCasingCreditFilters(): Observable<CasingCreditFiltersResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllCasingCreditsFilter}`;
    return this.http.get<CasingCreditFiltersResponse>(url);
  }

  getCasingCreditDetailsForCasingCreditCreate(): Observable<CreateCasingCredit> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getCasingCreditDetailsForCreateCasingCredit}`;
    return this.http.get<CreateCasingCredit>(url);
  }

  getRerunReworkReasons(filters: any, pageNumber: number, pageSize: number): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllRerunReworkReasons}`;
    const filteredParams = Object.entries(filters || {}).reduce((acc, [key, value]) => {
      if (value != null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as any);
    const params: any = {
      ...filteredParams,
      page: pageNumber,
      pageSize
    };
    return this.http.get<any>(url, { params });
  }

  getRerunReworkReasonsFilters(): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllRerunReworkReasonsFilter}`;
    return this.http.get<any>(url);
  }

  createCasingCredit(createCasingCredit: CreateCasingCredit): Observable<CasingCreditRepairFiltersResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.createCasingCredit}`;
    return this.http.post<CasingCreditRepairFiltersResponse>(url, createCasingCredit);
  }

  updateFreeRepairs(updateFreeRepair: UpdateFreeRepair, freeRepairTableId: string): Observable<CasingCreditRepairFiltersResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.updateFreeRepairs}/${freeRepairTableId}`;
    return this.http.post<CasingCreditRepairFiltersResponse>(url, updateFreeRepair);
  }

  deleteFreeRepair(freeRepairTableId: string): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.deleteFreeRepairTableRecord}/${freeRepairTableId}`;
    return this.http.delete<any>(url);
  }

  createFreeTableEntry(updateFreeRepair: UpdateFreeRepair): Observable<CasingCreditRepairFiltersResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.createFreeRepairTableRecord}`;
    return this.http.post<CasingCreditRepairFiltersResponse>(url, updateFreeRepair);
  }

  searchCasingCreditAssignToCustomer(
    searchString: string,
    page: number,
    limit: number
  ): Observable<PaginatedCasingCreditAssignedCustomerResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.searchCasingCreditAssignToCustomer}`;
    return this.http.get<PaginatedCasingCreditAssignedCustomerResponse>(url, {
      params: { searchString: searchString, userLogin: this.store.selectSnapshot(UserState.getUserName), page: page, pageSize: limit }
    });
  }

  updateCasingCreditDetails(payload: PaginatedCasingCreditDetails): Observable<PaginatedCasingCreditDetailsResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.updateCasingCreditDetails}`;
    return this.http.post<PaginatedCasingCreditDetailsResponse>(url, payload);
  }

  updateCustomMolds(payload: PaginatedCustomMoldInfo): Observable<PaginatedCustomMoldResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.updateCustomMolds}`;
    return this.http.post<PaginatedCustomMoldResponse>(url, payload);
  }

  getCasingGrades(filters: any): Observable<CasingGrade[]> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllGrades}`;
    const filteredParams = Object.entries(filters || {}).reduce((acc, [key, value]) => {
      if (value != null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as any);
    return this.http
      .get<{ data: { grades: CasingGrade[] } }>(url, { params: filteredParams })
      .pipe(map((response) => response.data.grades));
  }

  getCasingGradeFilters(): Observable<CasingGradeFiltersResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllGradeFilters}`;
    return this.http.get<CasingGradeFiltersResponse>(url);
  }

  updateCasingGrades(payload: CasingGrade[]): Observable<CasingGrade[]> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.updateGrades}`;
    return this.http.post<CasingGrade[]>(url, payload);
  }

  deleteCasingGrade(gradeId: string): Observable<void> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.deleteGrade}`;
    return this.http.post<void>(url, gradeId);
  }

  getNewCasingGradePk(): Observable<string> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getNewGradeId}`;
    return this.http.get<string>(url);
  }

  checkGradeTableRules(payload: string[]): Observable<string[]> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.checkGradeTableRules}`;
    return this.http.post<string[]>(url, payload);
  }

  getCasingGradeTableFilters(): Observable<CasingGradeTableFiltersResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllGradeTableFilters}`;
    return this.http.get<CasingGradeTableFiltersResponse>(url);
  }

  assignCustomerCasingGradeTable(casingGradeTablePk: string, customerFks: string[]): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.assignCasingGradeTableToCustomers}/${casingGradeTablePk}`;
    const request = {
      customerFks: customerFks
    };
    return this.http.post<any>(url, request);
  }

  updateCasingGradeTable(casingGradeTablePk: string, modifiedRows: any[]): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.updateCasingGradeSpecifications}/${casingGradeTablePk}`;
    return this.http.post<any>(url, modifiedRows);
  }

  getCasingGradeTables(filters: any, pageNumber: number, pageSize: number): Observable<PaginatedCasingGradeTableResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllCasingGradeTable}`;
    const filteredParams = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value != null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as any);
    const params: any = {
      ...filteredParams,
      page: pageNumber,
      pageSize
    };
    return this.http.get<PaginatedCasingGradeTableResponse>(url, { params });
  }

  casingGradeCodeExists(code: string): Observable<boolean> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.checkCasingGradeCode}`;
    return this.http.get<boolean>(url, { params: { code } });
  }

  searchCasingGradeTableAssignToCustomer(
    searchString: string,
    casingGradeTablePk: string,
    page: number,
    limit: number
  ): Observable<PaginatedCasingGradeTableAssignedCustomerResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.searchCasingGradeTableAssignToCustomer}`;
    return this.http.get<PaginatedCasingGradeTableAssignedCustomerResponse>(url, {
      params: {
        searchString: searchString,
        userLogin: this.store.selectSnapshot(UserState.getUserName),
        casingGradeTablePk,
        page: page,
        pageSize: limit
      }
    });
  }

  createCasingGradeTable(createCasingGradeTable: {
    code: string;
    description: string;
    casingGradeSpecificationDTOS: any[];
  }): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.createCasingGradeTable}`;
    return this.http.post<any>(url, createCasingGradeTable);
  }

  getFreeRepair(filters: any, pageNumber: number, pageSize: number): Observable<PaginatedFreeRepairResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllFreeRepairs}`;
    const filteredParams = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value != null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as any);
    const params: any = {
      ...filteredParams,
      page: pageNumber,
      pageSize
    };
    return this.http.get<PaginatedFreeRepairResponse>(url, { params });
  }

  getFreeRepairFilters(): Observable<FreeRepairFiltersResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllFreeRepairsFilter}`;
    return this.http.get<FreeRepairFiltersResponse>(url);
  }

  getCasingCreditRepairs(filters: any, pageNumber: number, pageSize: number): Observable<PaginatedCasingCreditRepairResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllCasingRepairs}`;

    const filteredParams = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value != null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as any);

    const params: any = {
      ...filteredParams,
      page: pageNumber,
      pageSize
    };

    return this.http.get<PaginatedCasingCreditRepairResponse>(url, { params });
  }

  getCasingCreditRepairFilters(): Observable<CasingCreditRepairFiltersResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllCasingCreditRepairsFilter}`;
    return this.http.get<CasingCreditRepairFiltersResponse>(url);
  }

  getCasingRepairDetails(
    filters: any,
    casingCreditRepairPk: string,
    pageNumber: number,
    pageSize: number
  ): Observable<PaginatedCasingCreditRepairDetailsResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllCasingCreditRepairDetails}`;

    const filteredParams = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value != null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as any);

    const params: any = {
      ...filteredParams,
      casingCreditRepairPk,
      page: pageNumber,
      pageSize
    };

    return this.http.get<PaginatedCasingCreditRepairDetailsResponse>(url, { params });
  }

  getCasingRepairDetailsSearchFilters(casingCreditRepairPk: string): Observable<CasingCreditRepairDetailsFiltersResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllCasingCreditRepairDetailsFilter}`;
    const params = {
      casingCreditRepairPk
    };
    return this.http.get<CasingCreditRepairDetailsFiltersResponse>(url, { params });
  }

  /**
   * Updates casing repair details with modified amount values
   * @param casingCreditRepairPk The primary key of the casing repair
   * @param modifiedRows Array of rows with updated amount values
   * @returns Observable with the API response
   */
  updateCasingRepairDetails(casingCreditRepairPk: string, modifiedRows: any[]): Observable<any> {
    return this.http.post<any>(
      `${environment.backOfficeUrl}${environment.serviceUris.updateCasingRepairDetails}/${casingCreditRepairPk}`,
      modifiedRows
    );
  }
  /**
   * Saves a casing credit repair with its repair details
   * @param data Object containing code, description, and array of repair details
   * @returns Observable with the API response
   */
  saveCasingCreditRepair(data: { code: string; description: string; repairDetails: any[] }): Observable<any> {
    return this.http.post<any>(`${environment.backOfficeUrl}${environment.serviceUris.saveCasingCreditRepair}`, data);
  }

  /**
   * Fetches all repairs with pagination support
   * @param page The page number
   * @param pageSize Number of items per page
   * @returns Observable with the paginated repairs response
   */
  getAllPaginatedRepairs(page: number, pageSize: number): Observable<any> {
    const params = {
      page: page.toString(),
      pageSize: pageSize.toString()
    };

    return this.http.get<any>(`${environment.backOfficeUrl}${environment.serviceUris.getAllPaginatedRepairs}`, { params });
  }

  getAllRepairs(filters: any, page: number, pageSize: number): Observable<any> {
    const filteredParams = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value != null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as any);

    const params: any = {
      ...filteredParams,
      page: page.toString(),
      pageSize: pageSize.toString()
    };

    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllRepairs}`;
    return this.http.get<any>(url, { params });
  }

  getRepairFilterValues(): Observable<RepairFiltersResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getRepairFilterValues}`;
    return this.http.get<RepairFiltersResponse>(url);
  }

  deleteCasingCredit(casingCreditPk: string): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.deleteCasingCredit}/${casingCreditPk}`;
    return this.http.delete<any>(url);
  }
  assignCustomerCasingCredit(casingCreditPk: string, customerFks: string[]): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.assignCustomerCasingCredit}/${casingCreditPk}`;
    const request = {
      customerFks: customerFks
    };
    return this.http.post<any>(url, request);
  }
  deleteCasingCreditRepair(casingCreditRepairPk: string): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.deleteCasingCreditRepair}/${casingCreditRepairPk}`;
    return this.http.delete<any>(url);
  }

  /**
   * Creates a copy of a casing credit with new code, description and the casing credit details
   * @param code New credit code for the copy
   * @param description New description for the copy
   * @param payload Object containing casingCreditDetails array
   * @param casingCreditPk Original casing credit primary key being copied
   * @returns Observable with the API response
   */
  copyCasingCreate(code: string, description: string, payload: { casingCreditDetails: any[] }, casingCreditPk: string): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.copyCasingCredit}/${casingCreditPk}`;
    // Create the complete payload with code and description
    // Add casingCreditPk as a query parameter
    const params = new HttpParams().set('code', code).set('description', description);
    return this.http.post<any>(url, payload, { params });
  }

  /**
   * Fetches reject reasons with pagination and filtering support
   * @param pageNumber The page number
   * @param pageSize Number of items per page
   * @param filters Optional filters
   * @returns Observable with the paginated reject reasons response
   */
  getRejectReasons(filters: any, pageNumber: number, pageSize: number): Observable<PaginatedRejectReasonResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getRejectReasons}`;

    const filteredParams = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value != null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as any);

    const params: any = {
      ...filteredParams,
      page: pageNumber,
      pageSize
    };

    return this.http.get<PaginatedRejectReasonResponse>(url, { params });
  }

  getRejectReasonFilters(): Observable<RejectReasonFiltersResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllRejectReasonFilters}`;
    return this.http.get<RejectReasonFiltersResponse>(url);
  }

  getAllCasingGradeSpecification(
    casingGradeTablePk: string,
    pageNumber: number,
    pageSize: number
  ): Observable<PaginatedCasingGradeSpecificationResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllCasingGradeSpecification}`;
    const params: any = {
      casingGradeTablePk,
      page: pageNumber,
      pageSize
    };
    return this.http.get<PaginatedCasingGradeSpecificationResponse>(url, { params });
  }

  deleteCasingGradeTable(casingGradeTablePk: string): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.deleteCasingGradeTable}/${casingGradeTablePk}`;
    return this.http.delete<any>(url);
  }

  deleteCasingGradeSpecification(casingGradeSpecPk: string): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.deleteCasingGradeSpecification}/${casingGradeSpecPk}`;
    return this.http.delete<any>(url);
  }

  updateRejectReasons(payload: PaginatedRejectReasonInfo): Observable<PaginatedRejectReasonResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.updateRejectReasons}`;
    return this.http.post<PaginatedRejectReasonResponse>(url, payload);
  }

  getPreferredTreads(filters: any, pageNumber: number, pageSize: number): Observable<PaginatedPreferredTreadSpecificationResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getPreferredTreads}`;

    const filteredParams = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value != null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as any);

    const params: any = {
      ...filteredParams,
      page: pageNumber,
      pageSize
    };

    return this.http.get<PaginatedPreferredTreadSpecificationResponse>(url, { params });
  }

  getPreferredTreadsFilters(): Observable<PreferredTreadsFiltersResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getPreferredTreadsFilters}`;
    return this.http.get<PreferredTreadsFiltersResponse>(url);
  }

  getPreferredTreadSpecifications(
    filters: any,
    pageNumber: number,
    pageSize: number,
    preferredTreadSpecificationPk: string
  ): Observable<PaginatedPreferredTreadResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getPreferredTreadSpecifications}`;

    const filteredParams = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value != null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as any);

    const params: any = {
      ...filteredParams,
      page: pageNumber,
      pageSize,
      preferredTreadSpecificationPk
    };

    return this.http.get<PaginatedPreferredTreadResponse>(url, { params });
  }

  updatePreferredTreads(payload: PreferredTread[]): Observable<PreferredTread[]> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.updatePreferredTreads}`;
    return this.http.post<PreferredTread[]>(url, payload);
  }

  updatePreferredTreadWithSpec(payload: PreferredTreadWithSpecRequest): Observable<PreferredTreadWithSpecResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.updatePreferredTreadWithSpec}`;
    return this.http.post<PreferredTreadWithSpecResponse>(url, payload);
  }

  canPreferredTreadSpecBeDeleted(payload: PreferredTreadSpecificationDelete): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.canPreferredTreadSpecBeDeleted}`;
    return this.http.post<any>(url, payload);
  }

  deletePreferredTread(preferredTreadPk: string): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.deletePreferredTread}/${preferredTreadPk}`;
    return this.http.delete<any>(url);
  }

  deletePreferredTreadSpec(preferredTreadSpecPk: string): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.deletePreferredTreadSpec}/${preferredTreadSpecPk}`;
    return this.http.delete<any>(url);
  }

  getNewPreferredTreadPk(): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getNewPreferredTreadPk}`;
    return this.http.get<string>(url);
  }

  updatePreferredTreadSpecs(payload: PreferredTreadSpecification): Observable<PreferredTreadSpecification> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.updatePreferredTreadSpecs}`;
    return this.http.post<PreferredTreadSpecification>(url, payload);
  }

  // ==================== Wheel and Tire Services ====================

  // Wheels
  getWheels(filters: any, pageNumber: number, pageSize: number): Observable<PaginatedWheelResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllWheels}`;
    const filteredParams = Object.entries(filters || {}).reduce((acc, [key, value]) => {
      if (value != null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as any);
    const params: any = {
      ...filteredParams,
      page: pageNumber,
      pageSize
    };
    return this.http.get<PaginatedWheelResponse>(url, { params });
  }

  getWheelFilters(): Observable<WheelFiltersResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllWheelFilters}`;
    return this.http.get<WheelFiltersResponse>(url);
  }

  updateWheels(payload: Wheel[]): Observable<Wheel[]> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.updateWheels}`;
    return this.http.post<Wheel[]>(url, payload);
  }

  deleteWheel(wheelPk: string): Observable<void> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.deleteWheel}`;
    return this.http.post<void>(url, wheelPk);
  }

  getNewWheelPk(): Observable<string> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getNewWheelPk}`;
    return this.http.get<string>(url);
  }

  // Wheel Colors
  getWheelColors(filters: any, pageNumber: number, pageSize: number): Observable<PaginatedWheelColorResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllWheelColors}`;
    const filteredParams = Object.entries(filters || {}).reduce((acc, [key, value]) => {
      if (value != null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as any);
    const params: any = {
      ...filteredParams,
      page: pageNumber,
      pageSize
    };
    return this.http.get<PaginatedWheelColorResponse>(url, { params });
  }

  getWheelColorFilters(): Observable<WheelColorFiltersResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllWheelColorFilters}`;
    return this.http.get<WheelColorFiltersResponse>(url);
  }

  updateWheelColors(payload: WheelColor[]): Observable<WheelColor[]> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.updateWheelColors}`;
    return this.http.post<WheelColor[]>(url, payload);
  }

  deleteWheelColor(wheelColorPk: string): Observable<void> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.deleteWheelColor}`;
    return this.http.post<void>(url, wheelColorPk);
  }

  getNewWheelColorPk(): Observable<string> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getNewWheelColorPk}`;
    return this.http.get<string>(url);
  }

  getCasingDefinitions(filters: any, pageNumber: number, pageSize: number): Observable<PaginatedCasingDefinitionResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllCasingDefinitions}`;
    const filteredParams = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value != null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as any);
    const params: any = {
      ...filteredParams,
      page: pageNumber,
      pageSize
    };
    return this.http.get<PaginatedCasingDefinitionResponse>(url, { params });
  }

  getCasingFitments(filters: any, pageNumber: number, pageSize: number): Observable<PaginatedCasingFitmentResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllCasingFitments}`;
    const filteredParams = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value != null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as any);
    const params: any = {
      ...filteredParams,
      page: pageNumber,
      pageSize
    };
    return this.http.get<PaginatedCasingFitmentResponse>(url, { params });
  }

  getFactoryCode(filters: any, pageNumber: number, pageSize: number): Observable<PaginatedFactoryCodeInfoResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllFactoryCodes}`;
    const filteredParams = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value != null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as any);
    const params: any = {
      ...filteredParams,
      page: pageNumber,
      pageSize
    };
    return this.http.get<PaginatedFactoryCodeInfoResponse>(url, { params });
  }

  getFactoryCodeFilters(): Observable<FactoryCodeFiltersResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getFactoryCodeFilters}`;
    return this.http.get<FactoryCodeFiltersResponse>(url);
  }

  getAdjustmentReasons(filters: any, page: number, limit: number): Observable<PaginatedAdjustmentReasonInfoResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllAdjustmentReasons}`;
    const filteredParams = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value != null && value !== '' && !(Array.isArray(value) && value.length === 0)) {
        acc[key] = value;
      }
      return acc;
    }, {} as any);
    const params: any = {
      ...filteredParams,
      page,
      pageSize: limit
    };
    return this.http.get<PaginatedAdjustmentReasonInfoResponse>(url, { params });
  }

  getAdjustmentReasonsFilters(): Observable<AdjustmentReasonFiltersResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAdjustmentReasonsFilters}`;
    return this.http.get<AdjustmentReasonFiltersResponse>(url);
  }

  getReferenceInformation(type: string): Observable<ReferenceInfoResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllRefInfos}`;
    const params: any = { type };
    return this.http.get<ReferenceInfoResponse>(url, { params });
  }

  // Casing Purchase
  getCasingPurchases(filters: any, pageNumber: number, pageSize: number): Observable<PaginatedCasingPurchaseResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllCasingPurchases}`;
    const filteredParams = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value != null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as any);
    const params: any = { ...filteredParams, page: pageNumber, pageSize };
    return this.http.get<PaginatedCasingPurchaseResponse>(url, { params });
  }

  getCasingPurchaseFilters(): Observable<CasingPurchaseFiltersResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllCasingPurchasesFilter}`;
    return this.http.get<CasingPurchaseFiltersResponse>(url);
  }

  getCasingPurchaseDetails(
    filters: any,
    casingPurchasePk: string,
    pageNumber: number,
    pageSize: number
  ): Observable<PaginatedCasingPurchaseDetailsResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getCasingPurchaseDetails}`;
    const filteredParams = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value != null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as any);
    const params: any = { ...filteredParams, casingPurchasePk, page: pageNumber, pageSize };
    return this.http.get<PaginatedCasingPurchaseDetailsResponse>(url, { params });
  }

  getCasingPurchaseDetailsSearchFilters(casingPurchasePk: string): Observable<CasingPurchaseDetailsFiltersResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getCasingPurchaseDetailsFilter}`;
    const params: any = { casingPurchasePk };
    return this.http.get<CasingPurchaseDetailsFiltersResponse>(url, { params });
  }

  getCasingPurchaseDetailsForCreateCasingPurchase(): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getCasingPurchaseDetailsForCreateCasingPurchase}`;
    return this.http.get<any>(url);
  }

  createCasingPurchase(createCasingPurchase: any): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.createCasingPurchase}`;
    return this.http.post<any>(url, createCasingPurchase);
  }

  casingPurchaseCodeExists(code: string): Observable<boolean> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.casingPurchaseCodeExists}`;
    return this.http.get<boolean>(url, { params: { code } });
  }

  updateCasingPurchaseDetails(payload: PaginatedCasingPurchaseDetails): Observable<PaginatedCasingPurchaseDetailsResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.updateCasingPurchaseDetails}`;
    return this.http.post<PaginatedCasingPurchaseDetailsResponse>(url, payload);
  }
}
